import { lazy, Suspense, type ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router';
import type { Permission } from '@hv/shared';
import { ApiError } from './api/client';
import { useMe } from './api/hooks';
import { AuthProvider, useAuth } from './components/AuthContext';
import { describeError } from './components/Notify';
import { AppShell } from './layout/AppShell';
import { homePath } from './layout/views';
import { AdvancementPage } from './pages/AdvancementPage';
import { DashboardPage } from './pages/DashboardPage';
import { DuplicatesPage } from './pages/DuplicatesPage';
import { LoginPage } from './pages/LoginPage';
import { MyRecommendationsPage } from './pages/MyRecommendationsPage';
import { MyReferralsPage } from './pages/MyReferralsPage';
import { NoAccessPage } from './pages/NoAccessPage';
import { PipelinePage } from './pages/PipelinePage';
import { MyRecommendationHistoryPage, RecommendationHistoryPage } from './pages/RecommendationHistoryPage';
import { ReferralsPage } from './pages/ReferralsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ReferralEditorPage } from './referrals/ReferralEditorPage';
import { ReferralReportPage } from './referrals/ReferralExport';
import { ReferralConfirmationPage } from './referrals/ReferralSubmission';

const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));

const spinner = (
  <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
    <CircularProgress aria-label="Loading" />
  </Box>
);

function Authenticated({ bare = false }: { bare?: boolean }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }
    return <Box sx={{ p: 4 }} role="alert">Could not load your session: {describeError(me.error)}</Box>;
  }
  return (
    <AuthProvider me={me.data}>
      {bare ? (
        <BareRoutes />
      ) : (
        <AppShell>
          <AppRoutes />
        </AppShell>
      )}
    </AuthProvider>
  );
}

function AppRoutes() {
  const { can } = useAuth();
  const home = homePath(can);
  if (!home) return <NoAccessPage />;
  const gate = (permission: Permission, element: ReactElement) => (can(permission) ? element : <Navigate to={home} replace />);
  return (
    <Routes>
      <Route path="/" element={<Navigate to={home} replace />} />
      <Route path="/pipeline" element={gate('households.read', <PipelinePage />)} />
      <Route path="/referrals" element={gate('referrals.review', <ReferralsPage />)} />
      <Route path="/dashboard" element={gate('households.read', <DashboardPage />)} />
      <Route path="/advancement" element={gate('households.read', <AdvancementPage />)} />
      <Route path="/duplicates" element={gate('duplicates.resolve', <DuplicatesPage />)} />
      <Route path="/recommendation-history" element={gate('referrals.review', <RecommendationHistoryPage />)} />
      <Route path="/reports" element={gate('referrals.review', <ReportsPage />)} />
      <Route path="/my-referrals" element={gate('referrals.own', <MyReferralsPage />)} />
      <Route path="/my-referrals/:id" element={gate('referrals.own', <ReferralEditorPage />)} />
      <Route path="/my-recommendations" element={gate('referrals.own', <MyRecommendationsPage />)} />
      <Route path="/my-recommendation-history" element={gate('referrals.own', <MyRecommendationHistoryPage />)} />
      <Route path="/my-referrals/:id/confirmation" element={gate('referrals.own', <ReferralConfirmationPage />)} />
      <Route
        path="/admin/:tab?"
        element={gate(
          'settings.read',
          <Suspense fallback={spinner}>
            <AdminPage />
          </Suspense>,
        )}
      />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

function BareRoutes() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  return can('referrals.review') ? <ReferralReportPage params={params} /> : <Navigate to="/" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/referrals/report" element={<Authenticated bare />} />
      <Route path="/*" element={<Authenticated />} />
    </Routes>
  );
}
